import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Section, Field, OptionCard, Choice, SliderField, TextField,
  formatPostal, formatName,
} from "@/components/onboarding/fields";
import { OnboardingRail, TrustStrip } from "@/components/onboarding/OnboardingRail";
import { VerifyStep } from "@/components/onboarding/VerifyStep";
import { Logo } from "@/components/nav/Brand";
import {
  PieChart, LineChart, Vault, Bitcoin, Boxes, Moon, Target, TrendingUp,
  Wallet as WalletIcon, GraduationCap, Sprout, Gauge, Users, Search, Share2,
  Newspaper, CalendarClock, Sparkles, CheckCircle2, ArrowRight, ArrowLeft, Save, Loader2,
} from "lucide-react";

const PRODUCTS = [
  { id: "managed", label: "Managed Portfolios", icon: PieChart },
  { id: "stocks", label: "Stocks & ETFs", icon: LineChart },
  { id: "savings", label: "USD High-Yield Savings", icon: Vault },
  { id: "halal", label: "Halal / Murabaha", icon: Moon },
  { id: "crypto", label: "Crypto", icon: Bitcoin },
  { id: "commodities", label: "Commodities & Forex", icon: Boxes },
];
const GOALS = [
  { id: "grow", label: "Grow my wealth", icon: TrendingUp },
  { id: "retirement", label: "Save for retirement", icon: Sprout },
  { id: "income", label: "Generate passive income", icon: WalletIcon },
  { id: "halal", label: "Invest the halal way", icon: Moon },
  { id: "purchase", label: "A big future purchase", icon: Target },
  { id: "education", label: "Education fund", icon: GraduationCap },
];
const EXPERIENCE = [
  { id: "new", label: "I'm new to investing", hint: "We'll keep things simple and explain as we go" },
  { id: "some", label: "I have some experience", hint: "You've bought shares or funds before" },
  { id: "pro", label: "I'm an experienced investor", hint: "You trade actively and know the risks" },
];
const RISK = [
  { id: "conservative", label: "Conservative", hint: "Protect capital, steady growth" },
  { id: "balanced", label: "Balanced", hint: "A mix of growth and stability" },
  { id: "aggressive", label: "Aggressive", hint: "Maximise long-term growth" },
];
const SOURCES = [
  { id: "friends", label: "Friends / family", icon: Users },
  { id: "google", label: "Google search", icon: Search },
  { id: "social", label: "Social media", icon: Share2 },
  { id: "news", label: "News / blog", icon: Newspaper },
  { id: "event", label: "Event / webinar", icon: CalendarClock },
  { id: "ai", label: "AI tools", icon: Sparkles },
];
const SOCIALS = ["Instagram", "Facebook", "YouTube", "TikTok", "LinkedIn", "X (Twitter)"];
const MARITAL = [
  { id: "single", label: "Single" }, { id: "married", label: "Married" },
  { id: "widowed", label: "Widowed" }, { id: "divorced", label: "Divorced" },
];
const EMPLOYMENT_STATUS = ["Employed", "Self-Employed", "Business Owner", "Unemployed", "Retired", "Student"];
const INDUSTRIES = [
  "Technology", "Finance & Banking", "Healthcare", "Education", "Real Estate",
  "Import/Export", "Retail & Trade", "Oil & Gas / Energy", "Construction",
  "Hospitality & Tourism", "Government", "Legal", "Media & Marketing",
  "Manufacturing", "Transportation & Logistics", "Agriculture", "Other",
];
const NEEDS_JOB = ["Employed", "Self-Employed", "Business Owner"];
const DISCLOSURES = [
  { id: "shareholder", label: "A senior executive or 10%+ shareholder of a publicly traded company" },
  { id: "pep", label: "A senior political figure (politically exposed person)" },
  { id: "finra", label: "Affiliated with a US-registered broker-dealer or FINRA" },
];
const COUNTRY_LIST = [
  "Qatar", "Saudi Arabia", "United Arab Emirates", "Kuwait", "Bahrain", "Oman",
  "United States", "United Kingdom", "Canada", "Australia", "Germany", "France",
  "Spain", "Italy", "Netherlands", "Switzerland", "Sweden", "Ireland", "Japan",
  "Singapore", "Hong Kong", "China", "India", "Pakistan", "Bangladesh",
  "Egypt", "Jordan", "Lebanon", "Turkey", "Morocco", "Nigeria", "Kenya",
  "South Africa", "Indonesia", "Malaysia", "Philippines", "Brazil", "Mexico", "Other",
];

const STEPS = [
  "Products", "Your goal", "Experience", "Risk appetite", "Your finances",
  "How you found us", "Citizenship", "Your address", "Date of birth", "Marital status",
  "Employment", "Disclosures", "Verify identity",
];
const PHASES = [
  { label: "Your preferences", hint: "Products, goals and risk", steps: [0, 1, 2, 3, 4] },
  { label: "About you", hint: "Identity and residence", steps: [5, 6, 7, 8, 9] },
  { label: "Compliance", hint: "Source of wealth checks", steps: [10, 11] },
  { label: "Verification", hint: "Document and selfie check", steps: [12] },
];
const phaseOf = (step) => PHASES.findIndex((p) => p.steps.includes(step));

const money = (n) => `$${Number(n).toLocaleString("en-US")}`;
const yearsAgo = (dob) => { if (!dob) return 0; return (Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000); };

// Per-step validation: one clear sentence per problem field.
const validate = (step, a, kyc) => {
  const e = {};
  if (step === 0 && a.products.length === 0) e.products = "Choose at least one product to continue.";
  if (step === 1 && !a.goal) e.goal = "Pick the goal that fits you best.";
  if (step === 2 && !a.experience) e.experience = "Let us know your experience level.";
  if (step === 3 && !a.risk) e.risk = "Select the risk level you're comfortable with.";
  if (step === 5) {
    if (!a.source) e.source = "Tell us how you found Tharwy.";
    else if (a.source === "social" && !a.source_detail) e.source_detail = "Which platform was it?";
  }
  if (step === 6) {
    if (!a.citizenship) e.citizenship = "Select your country of citizenship.";
    if (a.us_tax_resident === null) e.us_tax_resident = "Please answer yes or no.";
  }
  if (step === 7) {
    if (!a.address_country) e.address_country = "Select the country you live in.";
    if (!a.city || a.city.trim().length < 2) e.city = "Enter the city you live in.";
    if (!a.street && !a.building) e.street = "Enter a street address or a building name.";
  }
  if (step === 8) {
    if (!a.date_of_birth) e.date_of_birth = "Enter your date of birth.";
    else if (yearsAgo(a.date_of_birth) < 18) e.date_of_birth = "You must be at least 18 years old to open an account.";
    else if (yearsAgo(a.date_of_birth) > 110) e.date_of_birth = "Please check that date — it doesn't look right.";
  }
  if (step === 9 && !a.marital_status) e.marital_status = "Select your marital status.";
  if (step === 10) {
    if (!a.employment_status) e.employment_status = "Select your employment status.";
    else if (NEEDS_JOB.includes(a.employment_status)) {
      if (!a.job_title) e.job_title = "Enter your job title.";
      if (!a.employment_industry) e.employment_industry = "Select your industry.";
      if (!a.employer_country) e.employer_country = "Select where your employer is based.";
    }
  }
  if (step === 11 && a.disclosures.length === 0) e.disclosures = "Select an option — choose “None of these apply” if that's the case.";
  if (step === 12 && kyc !== "done") e.kyc = "Complete the identity check to finish.";
  return e;
};

export default function Onboarding() {
  const { submitOnboarding } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [kyc, setKyc] = useState("idle");     // idle | scanning | done
  const [docType, setDocType] = useState("passport");
  const [docMeta, setDocMeta] = useState({});
  const [touched, setTouched] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef();

  const [ans, setAns] = useState({
    products: [], goal: null, experience: null, risk: null, islamic_account: false,
    invest_amount: 10000, yearly_income: 70000, source: null, source_detail: null,
    citizenship: null, us_tax_resident: null,
    address_country: null, region: "", street: "", building: "", apartment: "", city: "", postal_code: "",
    date_of_birth: "", marital_status: null, employment_status: null, job_title: "", employment_industry: null,
    employer_country: null, disclosures: [],
  });

  // Resume a part-finished application.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/onboarding/draft");
        if (data.answers) {
          setAns((a) => ({ ...a, ...data.answers }));
          // A nudge email links to ?step=N; never past the furthest step actually saved.
          const asked = Number(new URLSearchParams(window.location.search).get("step"));
          const saved = data.step || 0;
          setStep(Math.min(Number.isFinite(asked) && asked > 0 ? Math.min(asked, saved) : saved,
                           STEPS.length - 1));
          setSavedAt(data.saved_at);
          toast.success("Welcome back", { description: "We picked up where you left off." });
        }
      } catch (e) { console.error("Draft load skipped:", e); }
      finally { setLoaded(true); }
    })();
  }, []);

  // Save-and-continue: debounced autosave of answers + position.
  useEffect(() => {
    if (!loaded) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.put("/auth/onboarding/draft", { answers: ans, step });
        setSavedAt(data.saved_at);
      } catch (e) { console.error("Autosave failed:", e); }
      finally { setSaving(false); }
    }, 900);
    return () => clearTimeout(timer.current);
  }, [ans, step, loaded]);

  const errors = validate(step, ans, kyc);
  const err = (k) => (touched ? errors[k] : undefined);

  const set = (patch) => setAns((a) => ({ ...a, ...patch }));
  const toggleProduct = (id) => set({
    products: ans.products.includes(id) ? ans.products.filter((p) => p !== id) : [...ans.products, id],
  });
  const toggleDisclosure = (id) => setAns((a) => {
    if (id === "none") return { ...a, disclosures: ["none"] };
    const has = a.disclosures.includes(id);
    return { ...a, disclosures: has ? a.disclosures.filter((x) => x !== id) : [...a.disclosures.filter((x) => x !== "none"), id] };
  });

  const startKyc = async () => {
    setKyc("scanning");
    try {
      const { data } = await api.post("/kyc/shufti/start");
      if (data.mode === "live" && data.verification_url) { window.location.assign(data.verification_url); return; }
      setTimeout(() => setKyc("done"), 2200);
    } catch (e) {
      setKyc("idle");
      toast.error("Verification unavailable", { description: e?.response?.data?.detail || "Please try again" });
    }
  };

  const saveNow = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/onboarding/draft", { answers: ans, step });
      setSavedAt(data.saved_at);
      toast.success("Progress saved", { description: "Sign back in anytime to continue." });
    } catch (e) { toast.error("Could not save your progress"); }
    finally { setSaving(false); }
  }, [ans, step]);

  const next = async () => {
    if (Object.keys(errors).length) {
      setTouched(true);
      toast.error("Please check the highlighted fields");
      return;
    }
    setTouched(false);
    if (step < STEPS.length - 1) { setStep((s) => s + 1); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setBusy(true);
    try {
      // The profile is only committed once the client explicitly finishes, so the
      // verified panel stays on screen until they tap Finish.
      await submitOnboarding(ans);
      try { await api.post("/kyc/shufti/complete"); } catch (e) { console.error("KYC complete (demo) skipped:", e); }
      toast.success("Identity verified — you're all set!", { description: "Welcome to Tharwy." });
      navigate("/", { replace: true });
    } catch (e) {
      toast.error("Could not finish", { description: e?.response?.data?.detail || "Please try again" });
    } finally { setBusy(false); }
  };

  const back = () => { setTouched(false); setStep((s) => Math.max(0, s - 1)); };

  const content = () => {
    if (step === 0) return (
      <Section title="What are you here to do?" subtitle="Pick everything that applies — you can change this later in your profile.">
        <div className="grid sm:grid-cols-2 gap-3.5">
          {PRODUCTS.map((p) => (
            <OptionCard key={p.id} active={ans.products.includes(p.id)} onClick={() => toggleProduct(p.id)}
              icon={p.icon} label={p.label} testid={`product-${p.id}`} />
          ))}
        </div>
        {err("products") && <p className="text-xs font-medium text-destructive mt-4" data-testid="products-error">{errors.products}</p>}

        <div className="mt-6 rounded-xl border p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Do you want a Shariah-compliant account?</div>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                We'll only show and allow screened instruments, never lend you money or let you
                sell what you don't own, and exchange currency on the spot with no rollover
                interest. You can change this any time in your profile.
              </p>
            </div>
            <Switch checked={!!ans.islamic_account} data-testid="onboarding-islamic-toggle"
              onCheckedChange={(v) => set("islamic_account", v)} />
          </div>
        </div>
      </Section>
    );
    if (step === 1) return (
      <Section title="What's your main goal?" subtitle="This shapes the portfolios we put in front of you.">
        <div className="grid sm:grid-cols-2 gap-3.5">
          {GOALS.map((g) => (
            <OptionCard key={g.id} active={ans.goal === g.id} onClick={() => set({ goal: g.id })}
              icon={g.icon} label={g.label} testid={`goal-${g.id}`} />
          ))}
        </div>
        {err("goal") && <p className="text-xs font-medium text-destructive mt-4">{errors.goal}</p>}
      </Section>
    );
    if (step === 2) return (
      <Section title="How much investing have you done?" subtitle="There's no wrong answer — we'll meet you where you are.">
        <div className="space-y-3.5">
          {EXPERIENCE.map((e) => (
            <OptionCard key={e.id} active={ans.experience === e.id} onClick={() => set({ experience: e.id })}
              label={e.label} hint={e.hint} testid={`exp-${e.id}`} />
          ))}
        </div>
        {err("experience") && <p className="text-xs font-medium text-destructive mt-4">{errors.experience}</p>}
      </Section>
    );
    if (step === 3) return (
      <Section title="How much risk suits you?" subtitle="We'll match this to a suitable portfolio blend — and you can change it anytime.">
        <div className="space-y-3.5">
          {RISK.map((r) => (
            <OptionCard key={r.id} active={ans.risk === r.id} onClick={() => set({ risk: r.id })}
              icon={Gauge} label={r.label} hint={r.hint} testid={`risk-${r.id}`} />
          ))}
        </div>
        {err("risk") && <p className="text-xs font-medium text-destructive mt-4">{errors.risk}</p>}
      </Section>
    );
    if (step === 4) return (
      <Section title="Tell us about your finances" subtitle="Rough figures are fine. Regulators require this to check that our products suit you.">
        <div className="space-y-10">
          <SliderField label="How much are you looking to invest?" value={ans.invest_amount} display={money(ans.invest_amount)}
            min={1000} max={1000000} step={1000} onChange={(v) => set({ invest_amount: v })} testid="slider-invest"
            hint="You can start smaller — this is only an indication." />
          <SliderField label="What is your total yearly income?" value={ans.yearly_income} display={`${money(ans.yearly_income)} a year`}
            min={10000} max={1000000} step={5000} onChange={(v) => set({ yearly_income: v })} testid="slider-income"
            hint="Before tax, from all sources." />
        </div>
      </Section>
    );
    if (step === 5) return (
      <Section title="How did you hear about us?" subtitle="It helps us reach more investors like you.">
        <div className="grid sm:grid-cols-2 gap-3.5">
          {SOURCES.map((s) => (
            <OptionCard key={s.id} active={ans.source === s.id} onClick={() => set({ source: s.id, source_detail: null })}
              icon={s.icon} label={s.label} testid={`source-${s.id}`} />
          ))}
        </div>
        {err("source") && <p className="text-xs font-medium text-destructive mt-4">{errors.source}</p>}
        {ans.source === "social" && (
          <div className="mt-7">
            <div className="text-sm font-semibold mb-3">Which platform?</div>
            <div className="flex flex-wrap gap-2" data-testid="social-detail">
              {SOCIALS.map((s) => (
                <Choice key={s} active={ans.source_detail === s} onClick={() => set({ source_detail: s })}>{s}</Choice>
              ))}
            </div>
            {err("source_detail") && <p className="text-xs font-medium text-destructive mt-3">{errors.source_detail}</p>}
          </div>
        )}
      </Section>
    );
    if (step === 6) return (
      <Section title="Where are you a citizen?" subtitle="Your citizenship and tax residency decide which markets and tax forms apply to you.">
        <div className="space-y-7 max-w-md">
          <Field label="Country of citizenship" required error={err("citizenship")}
            tooltip="The country that issued your passport" testid="kyc-citizenship-field">
            <Select value={ans.citizenship || ""} onValueChange={(v) => set({ citizenship: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="kyc-citizenship"><SelectValue placeholder="Select country" /></SelectTrigger>
              <SelectContent className="max-h-72">{COUNTRY_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Are you a US tax resident?" required error={err("us_tax_resident")}
            hint="Select “Yes” if you hold a US passport or green card, or file US taxes." testid="kyc-ustax-field">
            <div className="flex gap-2" data-testid="kyc-ustax">
              {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                <Choice key={lbl} active={ans.us_tax_resident === val} onClick={() => set({ us_tax_resident: val })}
                  testid={`kyc-ustax-${lbl.toLowerCase()}`}>{lbl}</Choice>
              ))}
            </div>
          </Field>
        </div>
      </Section>
    );
    if (step === 7) return (
      <Section title="Where do you live?" subtitle="Your residential address as it appears on a bank statement or utility bill.">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Field label="Country" required error={err("address_country")} testid="kyc-addr-country-field">
              <Select value={ans.address_country || ""} onValueChange={(v) => set({ address_country: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="kyc-addr-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-72">{COUNTRY_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Region / State" testid="kyc-region-field">
            <TextField value={ans.region} onChange={(v) => set({ region: v })} format={formatName}
              placeholder="e.g. Ad Dawhah" data-testid="kyc-region" />
          </Field>
          <Field label="City" required error={err("city")} testid="kyc-city-field">
            <TextField value={ans.city} onChange={(v) => set({ city: v })} format={formatName}
              placeholder="e.g. Doha" data-testid="kyc-city" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Street address" required error={err("street")} hint="Street name and area — as per your utility bill"
              testid="kyc-street-field">
              <TextField value={ans.street} onChange={(v) => set({ street: v })}
                placeholder="e.g. Al Sadd Street, Al Sadd" data-testid="kyc-street" />
            </Field>
          </div>
          <Field label="Building name" testid="kyc-building-field">
            <TextField value={ans.building} onChange={(v) => set({ building: v })} placeholder="e.g. Al Fardan Tower" data-testid="kyc-building" />
          </Field>
          <Field label="Apartment / Villa no." testid="kyc-apartment-field">
            <TextField value={ans.apartment} onChange={(v) => set({ apartment: v })} placeholder="e.g. 1204" data-testid="kyc-apartment" />
          </Field>
          <Field label="Postal code" hint="Leave blank if your country doesn't use one" testid="kyc-postal-field">
            <TextField value={ans.postal_code} onChange={(v) => set({ postal_code: v })} format={formatPostal}
              placeholder="e.g. 24449" data-testid="kyc-postal" />
          </Field>
        </div>
      </Section>
    );
    if (step === 8) return (
      <Section title="When were you born?" subtitle="You must be at least 18 to open a Tharwy account.">
        <div className="max-w-xs">
          <Field label="Date of birth" required error={err("date_of_birth")}
            hint="Use the date shown on your passport or ID" testid="kyc-dob-field">
            <Input type="date" max={new Date().toISOString().slice(0, 10)} value={ans.date_of_birth}
              onChange={(e) => set({ date_of_birth: e.target.value })} className="h-11 rounded-xl" data-testid="kyc-dob" />
          </Field>
        </div>
      </Section>
    );
    if (step === 9) return (
      <Section title="What's your marital status?" subtitle="Part of the standard regulatory checks for opening an account.">
        <div className="space-y-3.5 max-w-md">
          {MARITAL.map((m) => (
            <OptionCard key={m.id} active={ans.marital_status === m.id} onClick={() => set({ marital_status: m.id })}
              label={m.label} testid={`marital-${m.id}`} />
          ))}
        </div>
        {err("marital_status") && <p className="text-xs font-medium text-destructive mt-4">{errors.marital_status}</p>}
      </Section>
    );
    if (step === 10) return (
      <Section title="What do you do for a living?" subtitle="We ask to understand your source of wealth — a regulatory requirement for every account.">
        <div className="space-y-6 max-w-md">
          <Field label="Employment status" required error={err("employment_status")} testid="employment-status-field">
            <Select value={ans.employment_status || ""} onValueChange={(v) => set({ employment_status: v })}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="employment-status"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>{EMPLOYMENT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {/* Conditional: employer details only when they exist */}
          {NEEDS_JOB.includes(ans.employment_status) && (
            <>
              <Field label="Job title" required error={err("job_title")} hint="As on your contract or business licence" testid="employment-job-title-field">
                <TextField value={ans.job_title} onChange={(v) => set({ job_title: v })} placeholder="e.g. Finance Director" data-testid="employment-job-title" />
              </Field>
              <Field label="Industry" required error={err("employment_industry")} testid="employment-industry-field">
                <Select value={ans.employment_industry || ""} onValueChange={(v) => set({ employment_industry: v })}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="employment-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent className="max-h-72">{INDUSTRIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Employer's country" required error={err("employer_country")} testid="employment-country-field">
                <Select value={ans.employer_country || ""} onValueChange={(v) => set({ employer_country: v })}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="employment-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="max-h-72">{COUNTRY_LIST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </>
          )}
        </div>
      </Section>
    );
    if (step === 11) return (
      <Section title="Does any of this apply to you or a close relative?"
        subtitle="These don't apply to most people — but the regulator requires us to ask.">
        <div className="space-y-3.5">
          {DISCLOSURES.map((d) => (
            <OptionCard key={d.id} active={ans.disclosures.includes(d.id)} onClick={() => toggleDisclosure(d.id)}
              label={d.label} testid={`disclosure-${d.id}`} />
          ))}
          <OptionCard active={ans.disclosures.includes("none")} onClick={() => toggleDisclosure("none")}
            icon={CheckCircle2} label="None of these apply to me" testid="disclosure-none" />
        </div>
        {err("disclosures") && <p className="text-xs font-medium text-destructive mt-4">{errors.disclosures}</p>}
      </Section>
    );
    return (
      <VerifyStep kyc={kyc} startKyc={startKyc} docType={docType} setDocType={setDocType}
        docMeta={docMeta} setDocMeta={setDocMeta} onUploaded={() => setKyc("done")} />
    );
  };

  const phase = phaseOf(step);
  const pct = Math.round((step / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen grid lg:grid-cols-[380px_1fr] bg-background">
      <OnboardingRail phases={PHASES} currentPhase={phase} pct={pct} savedAt={savedAt} />

      <main className="flex flex-col min-h-screen">
        <div className="flex items-center gap-3 px-5 sm:px-10 h-16 border-b border-border">
          <div className="lg:hidden flex items-center gap-2 font-heading font-bold">
            <Logo className="h-12 w-auto" />
            Tharwy
          </div>
          <div className="ms-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="onboarding-saved">
              {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</>
                      : savedAt ? <><Save className="h-3.5 w-3.5" />Saved</> : null}
            </span>
            <span className="text-xs font-semibold text-muted-foreground" data-testid="onboarding-step-label">
              {PHASES[phase].label} · Step {step + 1} of {STEPS.length}
            </span>
          </div>
        </div>

        {/* Mobile progress */}
        <div className="lg:hidden px-5 sm:px-10 pt-5">
          <div className="flex items-center justify-between text-[11px] font-semibold mb-2">
            <span className="text-muted-foreground">{STEPS[step]}</span>
            <span className="numeric text-[color:var(--tharwy-lime-700)]">{pct}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-[color:var(--tharwy-lime-700)] transition-[width] duration-500"
              style={{ width: `${pct}%` }} data-testid="onboarding-progress-mobile" />
          </div>
        </div>

        <div className="flex-1 px-5 sm:px-10 py-9 max-w-3xl w-full">{content()}</div>

        <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 sm:px-10 py-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              {step > 0 && (
                <Button variant="outline" onClick={back} className="h-12 rounded-full gap-1.5 px-5" data-testid="onboarding-back">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
              <Button variant="ghost" onClick={saveNow} disabled={saving}
                className="h-12 rounded-full gap-1.5 hidden sm:inline-flex" data-testid="onboarding-save">
                <Save className="h-4 w-4" /> Save &amp; finish later
              </Button>
              <Button onClick={next} disabled={busy} className="ms-auto h-12 rounded-full gap-1.5 px-7 flex-1 sm:flex-none"
                data-testid="onboarding-continue">
                {step === STEPS.length - 1 ? (busy ? "Finishing…" : "Finish & open account") : "Continue"}
                {step < STEPS.length - 1 && <ArrowRight className="h-4 w-4" />}
              </Button>
            </div>
            <div className="mt-3 lg:hidden"><TrustStrip compact /></div>
          </div>
        </div>
      </main>
    </div>
  );
}
